'use client';

import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { ShopperSearch } from '@/scapi';
import { Button } from '@/components/ui/button';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import EatalyPlpFiltersPanel from '@/components/eataly-plp-filters/plp-filters-panel';
import { usePlpRefinements } from '@/components/eataly-plp-filters/hooks/use-plp-refinements';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/spinner';

const LG_UP = '(min-width: 1024px)';

export interface EatalyCategoryPlpFiltersDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    result: ShopperSearch.schemas['ProductSearchResult'];
    refine: string[];
    excludeRefinementAttributeIds?: readonly string[];
    categoryId?: string;
    showCategoryFilter?: boolean;
    /** Sticky-bar Filtra control; must live inside the drawer root so Vaul can move focus. */
    trigger?: ReactNode;
}

export default function EatalyCategoryPlpFiltersDrawer({
    open,
    onOpenChange,
    result,
    refine,
    excludeRefinementAttributeIds,
    categoryId,
    showCategoryFilter = false,
    trigger,
}: EatalyCategoryPlpFiltersDrawerProps): ReactElement {
    const { t } = useTranslation(['categoryPlp', 'category']);
    const isLgUp = useMediaQuery(LG_UP);
    const plp = usePlpRefinements({
        result,
        refine,
        excludeRefinementAttributeIds,
        categoryId,
        showCategoryFilter,
    });

    const isPending = plp.isPending;

    const countLabel = isPending ? (
        <Spinner size="sm" />
    ) : (
        t('category:banner.productsAvailable', { count: result.total ?? 0 })
    );

    const direction = isLgUp ? 'right' : 'bottom';

    const handleClearFilters = () => {
        plp.clearAllRefinements();
    };

    const handleShowProducts = () => {
        onOpenChange(false);
    };

    return (
        <Drawer autoFocus direction={direction} open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
            {trigger ? <DrawerTrigger asChild>{trigger}</DrawerTrigger> : null}
            <DrawerContent
                className={cn(
                    'bg-neutral-white border-sand-95',
                    'data-[vaul-drawer-direction=bottom]:max-h-[90vh]',
                    'data-[vaul-drawer-direction=right]:mt-0 data-[vaul-drawer-direction=right]:max-h-dvh data-[vaul-drawer-direction=right]:h-dvh',

                    'lg:data-[vaul-drawer-direction=right]:w-[calc((100vw-52px)/3+16px)]',
                    'lg:data-[vaul-drawer-direction=right]:max-w-[calc((100vw-52px)/3+16px)]',
                    'lg:data-[vaul-drawer-direction=right]:sm:max-w-[calc((100vw-52px)/3+16px)]'
                )}>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-start justify-between gap-4 p-6">
                        <DrawerTitle className="heading-title-l">{t('categoryPlp:filtersDrawer.title')}</DrawerTitle>
                        <DrawerClose asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-10 shrink-0 rounded-full border border-transparent hover:border-neutral-80"
                                aria-label={t('categoryPlp:filtersDrawer.closeAriaLabel')}>
                                <X className="size-6 text-neutral-10" aria-hidden />
                            </Button>
                        </DrawerClose>
                    </div>

                    <DrawerDescription className="sr-only">
                        {t('categoryPlp:filtersDrawer.description')}
                    </DrawerDescription>

                    <div className="min-h-0 flex-1 overflow-y-auto pb-4 pt-6">
                        <EatalyPlpFiltersPanel plp={plp} />
                    </div>

                    <DrawerFooter className="shrink-0 border-t border-neutral-80 bg-neutral-white px-6 pb-6 pt-4">
                        <div className="flex w-full gap-2">
                            <Button
                                type="button"
                                hierarchy="secondary"
                                mode="light"
                                size="large"
                                className="flex-1 uppercase cursor-pointer"
                                onClick={handleClearFilters}
                                disabled={isPending}>
                                {t('categoryPlp:filtersDrawer.clearFilters')}
                            </Button>
                            <Button
                                type="button"
                                hierarchy="primary"
                                mode="light"
                                size="large"
                                className="flex-1 uppercase cursor-pointer"
                                onClick={handleShowProducts}
                                disabled={isPending}>
                                {countLabel}
                            </Button>
                        </div>
                    </DrawerFooter>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
